import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  ClockIcon,
  BellIcon,
  CurrencyYenIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

const EmailSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    email_enabled: true,
    job_notifications: true,
    application_updates: true,
    payment_notifications: true,
    shift_reminders: true,
    promotional_emails: false,
    weekly_summary: true,
    frequency: 'immediate', // immediate, daily, weekly
    quiet_hours: {
      enabled: false,
      start_time: '22:00',
      end_time: '08:00'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const candidates = ['/users/settings/email', '/user/settings/email', '/settings/email'];
      let loaded = false;
      for (const path of candidates) {
        try {
          const res = await authApi.get(path);
          const data = res?.data;
          if (data && typeof data === 'object') {
            setSettings(prev => ({ ...prev, ...data }));
            loaded = true;
            break;
          }
        } catch (_) {}
      }
      if (!loaded) {
        setSettings(prev => ({ ...prev }));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    if (saving) return;
    const prev = settings;
    setSettings(newSettings);
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const candidates = ['/users/settings/email', '/user/settings/email', '/settings/email'];
      let ok = false;
      for (const path of candidates) {
        try {
          const res = await authApi.put(path, newSettings);
          if (res && res.status >= 200 && res.status < 300) { ok = true; break; }
        } catch (_) {}
      }
      if (!ok) {
        setSettings(prev);
        toast.error('設定の保存に失敗しました');
        return;
      }
      toast.success('設定を保存しました');
    } catch (error) {
      setSettings(prev);
      toast.error('設定の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key) => {
    const newSettings = {
      ...settings,
      [key]: !settings[key]
    };
    await saveSettings(newSettings);
  };

  const handleFrequencyChange = async (frequency) => {
    const newSettings = {
      ...settings,
      frequency
    };
    await saveSettings(newSettings);
  };

  const handleQuietHoursToggle = async () => {
    const newSettings = {
      ...settings,
      quiet_hours: {
        ...settings.quiet_hours,
        enabled: !settings.quiet_hours.enabled
      }
    };
    await saveSettings(newSettings);
  };

  const handleTimeChange = async (timeType, value) => {
    const newSettings = {
      ...settings,
      quiet_hours: {
        ...settings.quiet_hours,
        [timeType]: value
      }
    };
    await saveSettings(newSettings);
  };

  const emailTypes = [
    {
      key: 'job_notifications',
      label: '新着求人通知',
      description: 'あなたの条件に合った新しい求人が投稿された時',
      icon: BriefcaseIcon,
      color: 'blue'
    },
    {
      key: 'application_updates',
      label: '応募状況の更新',
      description: '応募した求人の採用・不採用などの状況変更時',
      icon: BellIcon,
      color: 'yellow'
    },
    {
      key: 'payment_notifications',
      label: '報酬の入金通知',
      description: 'ウォレットに報酬が入金された時',
      icon: CurrencyYenIcon,
      color: 'green'
    },
    {
      key: 'shift_reminders',
      label: 'シフトリマインダー',
      description: '勤務開始の24時間前と1時間前にお知らせ',
      icon: ClockIcon,
      color: 'purple'
    },
    {
      key: 'weekly_summary',
      label: '週間サマリー',
      description: '週間の活動内容や新着求人のまとめ',
      icon: ChatBubbleLeftEllipsisIcon,
      color: 'indigo'
    },
    {
      key: 'promotional_emails',
      label: 'キャンペーン・お知らせ',
      description: '特別なキャンペーンやサービスの更新情報',
      icon: ChatBubbleLeftEllipsisIcon,
      color: 'orange'
    }
  ];

  const frequencies = [
    { value: 'immediate', label: '即座に送信', description: '各イベント発生時にすぐ送信' },
    { value: 'daily', label: '1日1回まとめて', description: '午前9時に前日分をまとめて送信' },
    { value: 'weekly', label: '週1回まとめて', description: '月曜日の午前9時に前週分をまとめて送信' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">お知らせメール設定</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 基本設定 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center mb-4">
            <ChatBubbleLeftEllipsisIcon className="w-6 h-6 mr-3 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">基本設定</h2>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">メール通知</h3>
              <p className="text-sm text-gray-600">すべてのメール通知の有効・無効を切り替えます</p>
              <p className="text-sm text-gray-500">送信先: {user?.email}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.email_enabled}
                onChange={() => handleToggle('email_enabled')}
                className="sr-only peer"
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {/* 通知の種類 */}
        {settings.email_enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">通知の種類</h2>
            
            <div className="space-y-4">
              {emailTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <div key={type.key} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className={`p-2 rounded-lg mr-4 bg-${type.color}-100`}>
                        <IconComponent className={`w-5 h-5 text-${type.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{type.label}</h3>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings[type.key]}
                        onChange={() => handleToggle(type.key)}
                        className="sr-only peer"
                        disabled={saving}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 送信頻度 */}
        {settings.email_enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">送信頻度</h2>
            
            <div className="space-y-3">
              {frequencies.map((freq) => (
                <label
                  key={freq.value}
                  className={`relative flex items-center p-4 cursor-pointer rounded-lg border-2 transition-colors ${
                    settings.frequency === freq.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="frequency"
                    value={freq.value}
                    checked={settings.frequency === freq.value}
                    onChange={() => handleFrequencyChange(freq.value)}
                    className="sr-only"
                    disabled={saving}
                  />
                  <div className="flex-1">
                    <h3 className={`font-medium ${
                      settings.frequency === freq.value ? 'text-blue-700' : 'text-gray-900'
                    }`}>
                      {freq.label}
                    </h3>
                    <p className={`text-sm ${
                      settings.frequency === freq.value ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                      {freq.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* サイレント時間 */}
        {settings.email_enabled && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">サイレント時間</h2>
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">サイレント時間を設定</h3>
                <p className="text-sm text-gray-600">指定した時間帯はメール送信を停止します</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.quiet_hours.enabled}
                  onChange={handleQuietHoursToggle}
                  className="sr-only peer"
                  disabled={saving}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.quiet_hours.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    開始時間
                  </label>
                  <input
                    type="time"
                    value={settings.quiet_hours.start_time}
                    onChange={(e) => handleTimeChange('start_time', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    終了時間
                  </label>
                  <input
                    type="time"
                    value={settings.quiet_hours.end_time}
                    onChange={(e) => handleTimeChange('end_time', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={saving}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* 注意事項 */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">メール通知について</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> メールアドレスの変更はプロフィール設定から行えます</li>
                <li> サイレント時間中でも緊急性の高い通知は配信される場合があります</li>
                <li> 迷惑メールフォルダもご確認ください</li>
                <li> メール配信停止はいつでも設定できます</li>
                <li> まとめて送信する場合は、個別の通知は停止されます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;